import { keyValueMirror } from 'store/storeFunctions';
import { setGuidURL } from './canvasActions';
import { exists } from 'utils';
import { addMessage, clearMessages } from 'store/actions/appActions';
import constants from 'constants';

const { MESSAGE_TYPES, EDIT_MODEL_FORMAT, FORMAT_AVAILABLE } = constants;
export const actions = keyValueMirror(
  'DOWNLOAD_MODEL',
  'SET_PREVIEW_URL',
  'CLEAR_PREVIEW_URL',
  'SET_DEBUG_TOKEN',
  'SET_MODEL_EDITABLE',
);

export const setModelEditable = payload => ({
  type: actions.SET_MODEL_EDITABLE,
  payload,
});

// Silent prerequisite check for the edit button. Never surfaces a message —
// a model that cannot be edited should simply not offer the button.
export const checkModelEditable = args => async dispatch => {
  const { guid } = args;

  try {
    const url = `https://apiprod.archvision.services/models/${guid}`;

    const options = {
      method: 'GET',
      headers: { accept: 'application/json' },
    };

    const res = await fetch(url, options);

    if (!res.ok) {
      dispatch(setModelEditable(false));
      return;
    }

    const data = await res.json();
    const editFormat = data?.formats?.find(format => format.key === EDIT_MODEL_FORMAT);

    dispatch(setModelEditable(editFormat?.availability === FORMAT_AVAILABLE));
  } catch (error) {
    console.error(error);
    dispatch(setModelEditable(false));
  }
};

export const setDebugToken = payload => ({
  type: actions.SET_DEBUG_TOKEN,
  payload,
});

export const getDebugToken = args => async dispatch => {
  const { onSuccess } = args;

  try {
    const url = 'https://apiprod.archvision.services/clients/auth/token';
    const payload = {
      grant_type: 'client_credentials',
      client_id: '8c5dcdc906ea6dd5',
      /* eslint-disable-next-line */
      client_secret: 'ef21b2b28d0b95fede2f3ec6cbc9e25f9ebf8623a48c6488e9beb436dd31d4a054c357a9da397c8dd1527b778152b54d9444cc1aa76f39e7796033dea8c30067',
    };

    const options = {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(payload),
    };

    const res = await fetch(url, options);
    const data = await res.json();
    const debugToken = data?.access_token;

    dispatch(setDebugToken(debugToken));
    onSuccess?.(debugToken);
  } catch (error) {
    console.error(error);
  }
};

export const downloadModel = args => async dispatch => {
  const { guid, format, onComplete, showModal, onNotFound, onRetry } = args;

  try {
    const url = `https://apiprod.archvision.services/models/${guid}/download/${format}`;

    const options = {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    const messageConfig = {
      type: MESSAGE_TYPES.loading,
      showModal: exists(showModal) ? showModal : true,
      currentStep: 1,
      totalSteps: 2,
      closeButton: false,
    };

    const linkingModel = {
      message: 'Linking to model...',
      config: messageConfig,
    };

    const modelNotFound = {
      message: 'Unable to find model.',
      config: {
        type: MESSAGE_TYPES.error,
        showModal: true,
        closeButton: false,
      },
    };

    const loadFailed = {
      message: 'Unable to load model.',
      config: {
        type: MESSAGE_TYPES.error,
        action: onRetry && { label: 'Retry', callback: onRetry },
      },
    };

    dispatch(addMessage([ linkingModel ]));

    const res = await fetch(url, options);

    // A 404 here usually means the requested format has not been generated yet,
    // so the caller gets to run the job-status chain instead of dead-ending.
    if (res.status === 404) {
      dispatch(clearMessages());

      if (onNotFound) {
        onNotFound();
        return;
      }

      dispatch(addMessage([ modelNotFound ]));
      return;
    }

    if (!res.ok) {
      dispatch(clearMessages());
      dispatch(addMessage([ loadFailed ]));
      return;
    }

    const data = await res.json();
    dispatch(setGuidURL(data.url));
  } catch (error) {
    console.error(error);
  } finally {
    onComplete?.();
  }
};